/**
 * This file contains the contract types for structured responses.
 * It defines the data format for structured outputs from the AI system to clients.
 */

import { SmafCreatedData, TChartType } from './common-types';

// Constants for response types
export const RESPONSE_TYPES = {
    FLOW_TEMPLATE_CREATION: 'flow_template_creation' as const,
    IMAGE: 'image' as const,
    REDIRECT_TO_SUPPORT: 'redirect_to_support' as const,
    INSPIRATIONS: 'inspirations' as const,
    PRIORITY_LIST: 'priority_list' as const,
    SMAF_CREATED: 'smaf_created' as const,
    PDF_CLASSIFIER: 'pdf_classifier' as const,
    BUSINESS_TRENDS: 'business_trends' as const,
    COMPOSED_EMAIL: 'composed_email' as const,
    PIPELINE_STAGES: 'customize_pipeline_stages' as const,
    SPECIALIST: 'onboarding_specialist' as const,
    CALL_TO_ACTION: 'call_to_action' as const
};

// Supported structured response types
export type StructuredResponseType =
    (typeof RESPONSE_TYPES)[keyof typeof RESPONSE_TYPES];

// Proactive assistant response types
export const PROACTIVE_ASSISTANT_RESPONSE_TYPES: StructuredResponseType[] = [
    RESPONSE_TYPES.PRIORITY_LIST,
    RESPONSE_TYPES.BUSINESS_TRENDS
];

// Full structured response with type discriminator
export type StructuredResponse = {
    [T in StructuredResponseType]: {
        type: T;
        data: GetStructuredResponseData<T>;
    };
}[StructuredResponseType];

// Maps response types to their data structures
export type StructuredResponseDataMap = {
    [RESPONSE_TYPES.FLOW_TEMPLATE_CREATION]: FlowTemplateCreationData;
    [RESPONSE_TYPES.INSPIRATIONS]: IChatInspiration[];
    [RESPONSE_TYPES.REDIRECT_TO_SUPPORT]: RedirectToSupportData;
    [RESPONSE_TYPES.IMAGE]: Record<string, never>;
    [RESPONSE_TYPES.PRIORITY_LIST]: PriorityListData;
    [RESPONSE_TYPES.PDF_CLASSIFIER]: PdfClassifierData;
    [RESPONSE_TYPES.SMAF_CREATED]: SmafCreatedData;
    [RESPONSE_TYPES.BUSINESS_TRENDS]: BusinessTrendsData;
    [RESPONSE_TYPES.COMPOSED_EMAIL]: ComposedEmailData;
    [RESPONSE_TYPES.PIPELINE_STAGES]: PipelineStagesData;
    [RESPONSE_TYPES.CALL_TO_ACTION]: CallToActionData;
};

/**
 * Structured response data types
 */
export type FlowTemplateCreationData = {
    flow_type: string;
};

export type RedirectToSupportData = {
    message: string;
};

type ActionItem = {
    type: 'action_item';
    content: {
        text: string;
        action: { type: string; args?: Record<string, string> };
    };
};

type ChartItem = {
    type: 'chart';
    content: {
        type: TChartType;
        data: any;
    };
};

type ItemType = ActionItem | ChartItem;

export type PriorityListData = {
    title: string;
    sections: Array<{
        title: string;
        items: Array<ItemType>;
    }>;
};

export type PdfClassifierData = {
    has_contract: boolean;
};

export type BusinessTrendsData = {
    title: string;
    intro: string;
    trends_section_intro: string;
    outro: string;
    sections: Array<{
        title: string;
        items: Array<{
            title: string;
            description: string;
        }>;
    }>;
    stats: Array<{
        title: string;
        subtitle?: string;
        description?: string;
        charts?: any[];
    }>;
};

export type ComposedEmailData = {
    body: string;
    subject: string;
};

export type PipelineStagesData = {
    pipeline_stages: Array<{
        name: string;
        order: number;
        group: 'opportunities' | 'projects';
    }>;
    status?: 'approved';
};

export type SpecialistContextData = {
    onboarding_specialist_notes: SpecialistData;
};

export type SpecialistData = {
    business_name: string[];
    company_size: string[];
    business_goals: string[];
    recommended_tools: string[];
};

export type FileGenerationData = {
    flow_id: string | null;
};

export type CallToActionData = {
    type: string;
    text?: string;
    args?: Record<string, string>;
};

export interface IChatInspiration {
    category: string;
    content: string;
}

// ----------------
// Structured Response Utilities
// ----------------

// Returns the data type for a structured response type
export type GetStructuredResponseData<T extends StructuredResponseType> =
    T extends keyof StructuredResponseDataMap
        ? StructuredResponseDataMap[T]
        : {};

/* ------------------- PREVIEW CONTEXT ------------------- */
export type SubTaskResponse<T extends string, D> = {
    sub_task: {
        type: T;
        data: D;
    };
};

export type PreviewContextResponse =
    | SubTaskResponse<'customize_pipeline_stages', PipelineStagesData>
    | SubTaskResponse<'onboarding_specialist', SpecialistContextData>
    | SubTaskResponse<'file_generation', FileGenerationData>
    | null;
